import React from 'react';
import { X, Crown, Calendar, Zap, ArrowRight } from 'lucide-react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';

interface CustomInterviewPaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  usageDetails: {
    currentMonthCount: number;
    monthlyLimit: number;
    resetDate: string;
    userPlan: string;
  };
}

export const CustomInterviewPaywallModal: React.FC<CustomInterviewPaywallModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  usageDetails
}) => {
  // Format the reset date to be more user-friendly
  const formatResetDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 1) {
      return 'tomorrow';
    } else if (diffDays <= 7) {
      return `in ${diffDays} days`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric'});
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="540px" title="Custom Interview Monthly Limit"
    hideCloseButton={true}
    preventOutsideClick={true}>
      <div className="p-6">
        {/* Header with Crown Icon */}
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center mb-4">
            <Crown size={32} className="text-primary-light dark:text-primary-dark" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
            Upgrade to Unlock More Custom Interviews
          </h2>
          <p className="text-base text-text-secondary-light dark:text-text-secondary-dark">
            You've reached your free plan limit for custom interviews this month
          </p>
        </div>

        {/* Usage Statistics */}
        <div className="mb-6 p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/70 dark:bg-background-dark/70">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
              Custom Interviews Used
            </span>
            <span className="text-base font-bold text-text-primary-light dark:text-text-primary-dark">
              {usageDetails.currentMonthCount} / {usageDetails.monthlyLimit}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-border-light dark:bg-border-dark rounded-full h-2 mb-3">
            <div 
              className="bg-primary-light dark:bg-primary-dark h-2 rounded-full transition-all duration-300"
              style={{ width: '100%' }}
            ></div>
          </div>
          
          <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark">
            <Calendar size={16} className="mr-2" />
            <span>Resets {formatResetDate(usageDetails.resetDate)}</span>
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-6">
          <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">
            Subscribe for Unlimited Access
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center text-text-secondary-light dark:text-text-secondary-dark">
              <div className="w-6 h-6 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <Zap size={14} className="text-primary-light dark:text-primary-dark" />
              </div>
              <span className="text-sm">Unlimited custom interviews every month</span>
            </div>
            
            <div className="flex items-center text-text-secondary-light dark:text-text-secondary-dark">
              <div className="w-6 h-6 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <Zap size={14} className="text-primary-light dark:text-primary-dark" />
              </div>
              <span className="text-sm">Practice with any job description or company</span>
            </div>
            
            <div className="flex items-center text-text-secondary-light dark:text-text-secondary-dark">
              <div className="w-6 h-6 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <Zap size={14} className="text-primary-light dark:text-primary-dark" />
              </div>
              <span className="text-sm">Access to all premium interview features</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="primary" 
            fullWidth 
            rightIcon={<ArrowRight size={16} />}
            onClick={onUpgrade}
            trackingId="upgrade_custom_interview_paywall"
            trackingLocation="Custom Interview Paywall Modal"
          >
            Subscribe Now
          </Button>
          
          <Button 
            variant="outline" 
            fullWidth
            onClick={onClose}
            trackingId="dismiss_custom_interview_paywall"
            trackingLocation="Custom Interview Paywall Modal"
          >
            Maybe Later
          </Button>
        </div>
        
        {/* Footer Note */}
        <p className="text-xs text-center text-text-secondary-light dark:text-text-secondary-dark mt-4">
          Your limit will reset on <span className="text-text-primary-light dark:text-text-primary-dark">{formatResetDate(usageDetails.resetDate)}</span>
        </p>
      </div>
    </Modal>
  );
};

export default CustomInterviewPaywallModal; 